import type { TaskStatus } from './enums'
import { daysBetween } from './iso-week'
import { isOpen } from './visibility'

export type DueKind = 'sem-prazo' | 'fechada' | 'tranquila' | 'proxima' | 'hoje' | 'atrasada'

export interface DueState {
  readonly kind: DueKind
  /** Dias até o prazo (negativo = atrasada). Nulo sem prazo. */
  readonly days: number | null
}

/** Faltando até este número de dias, o prazo fica em destaque (`proxima`). */
export const DUE_SOON_DAYS = 2

/**
 * Estado do prazo de uma tarefa. Só tarefa aberta tem contagem — realizada ou
 * cancelada não "atrasa" mais. `today` em `YYYY-MM-DD` (America/Sao_Paulo).
 * `soonDays` vem de Configurações → Prazos e avisos.
 */
export function dueState(
  prazo: string | null | undefined,
  today: string,
  status: TaskStatus,
  soonDays: number = DUE_SOON_DAYS,
): DueState {
  if (!prazo) return { kind: 'sem-prazo', days: null }
  const days = daysBetween(today, prazo)
  if (!isOpen(status)) return { kind: 'fechada', days }
  if (days < 0) return { kind: 'atrasada', days }
  if (days === 0) return { kind: 'hoje', days }
  if (days <= soonDays) return { kind: 'proxima', days }
  return { kind: 'tranquila', days }
}

const pad = (n: number) => String(n).padStart(2, '0')

/** Texto do contador de prazo — o mesmo do protótipo, sem emoji. */
export function dueLabel(state: DueState): string | null {
  const { kind, days } = state
  if (days === null || kind === 'sem-prazo' || kind === 'fechada') return null
  if (kind === 'hoje') return 'Prazo acaba hoje'
  if (kind === 'atrasada') {
    const late = -days
    return `${pad(late)} ${late === 1 ? 'dia atrasado' : 'dias atrasado'}`
  }
  return days === 1 ? 'Falta 01 dia' : `Faltam ${pad(days)} dias`
}
